import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Combobox from '../components/Combobox';
import { useFlow, EMPTY_FORM, type FormData } from '../state/flow';
import { sortLocalized } from '../lib/sortLocalized';
import { checkNames } from '../lib/namecheck';
import { trackEvent } from '../lib/analytics';
import { FACT_ORDER } from '../lib/factOrder';
import './IDCardForm.scss';

// `clubs` is updated via toggleClub, never through `update()`.
type FieldKey = Exclude<keyof FormData, 'clubs'>;

export function FormLeft() {
  const { t, i18n } = useTranslation();
  // Plain navigate (no view transition) so the CSS flex transition on the
  // panes is visible during /id-card → /photo.
  const navigate = useNavigate();
  const { form, setForm, setIdFormTouched } = useFlow();

  // Each visit starts pristine; editing any field flips the flag so the
  // header's leave-page confirm only fires once there's unsaved input.
  useEffect(() => {
    setIdFormTouched(false);
  }, [setIdFormTouched]);

  const [local, setLocal] = useState<FormData>(form.firstName ? form : EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const locale = i18n.resolvedLanguage ?? 'en';
  const lists = useMemo(
    () => ({
      majors: sortLocalized(t('majors', { returnObjects: true }) as string[], locale),
      minors: sortLocalized(t('minors', { returnObjects: true }) as string[], locale),
      clubs: sortLocalized(t('clubs', { returnObjects: true }) as string[], locale),
    }),
    [t, locale],
  );

  const isComplete =
    !!local.firstName.trim() &&
    !!local.lastName.trim() &&
    !!local.major &&
    !!local.minor &&
    local.clubs.length > 0;

  function update<K extends FieldKey>(key: K, value: FormData[K]) {
    setIdFormTouched(true);
    setLocal((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  // Toggle a club in/out of `local.clubs`, capped at 4. Adding past the cap
  // is a no-op — Combobox already disables those rows visually.
  function toggleClub(option: string) {
    setIdFormTouched(true);
    setLocal((prev) => {
      const has = prev.clubs.includes(option);
      if (!has && prev.clubs.length >= 4) return prev;
      return {
        ...prev,
        clubs: has ? prev.clubs.filter((c) => c !== option) : [...prev.clubs, option],
      };
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isComplete || submitting) return;
    setSubmitting(true);
    const result = await checkNames(local.firstName.trim(), local.lastName.trim());
    setSubmitting(false);
    if (!result.ok && result.field) {
      trackEvent('namecheck_blocked', { field: result.field });
      setErrors({ [result.field]: t('form.errors.name') });
      return;
    }
    setForm({
      ...local,
      firstName: local.firstName.trim(),
      lastName: local.lastName.trim(),
    });
    trackEvent('form_submitted');
    navigate('/photo');
  }

  return (
    <div className="form-stack">
      <div className="form__head">
        <div className="form__head-inner">
          <p className="form__kicker">{t('form.kicker')}</p>
          <h1 className="form__title">{t('form.title')}</h1>
        </div>
      </div>
      <div className="form__body">
        <form className="form" onSubmit={handleSubmit} noValidate>
          <p className="form__intro">{t('form.intro')}</p>

          <div className="form__grid">
            <label className="form__field">
              <span className="visually-hidden">{t('form.firstName')}</span>
              <input
                type="text"
                placeholder={t('form.firstName')}
                value={local.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                aria-invalid={!!errors.firstName}
                autoComplete="given-name"
                maxLength={30}
              />
              {errors.firstName && <span className="form__error">{errors.firstName}</span>}
            </label>

            <label className="form__field">
              <span className="visually-hidden">{t('form.lastName')}</span>
              <input
                type="text"
                placeholder={t('form.lastName')}
                value={local.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                aria-invalid={!!errors.lastName}
                autoComplete="family-name"
                maxLength={30}
              />
              {errors.lastName && <span className="form__error">{errors.lastName}</span>}
            </label>

            <Combobox
              label={t('form.major')}
              value={local.major}
              options={lists.majors.filter((m) => m !== local.minor)}
              onChange={(v) => update('major', v)}
            />
            <Combobox
              label={t('form.minor')}
              value={local.minor}
              options={lists.minors.filter((m) => m !== local.major)}
              onChange={(v) => update('minor', v)}
            />
            <Combobox
              label={t('form.club')}
              multiple
              selected={local.clubs}
              options={lists.clubs}
              onToggle={toggleClub}
              max={4}
            />
          </div>

          <button
            type="submit"
            className="form__submit"
            disabled={!isComplete || submitting}
            aria-busy={submitting}
          >
            {t('common.next')}
          </button>
        </form>
      </div>
    </div>
  );
}

export function FormRight() {
  const { t } = useTranslation();
  // The third entry of FACT_ORDER is the fun fact /loading didn't get to show
  // this session — surface it here on desktop instead of the photo intro copy.
  const facts = t('loading.facts', { returnObjects: true }) as string[];
  const fact = facts[FACT_ORDER[2]] ?? t('photoIntro.title');
  return (
    <div className="form__photo-intro">
      <p>{fact}</p>
    </div>
  );
}
